
import { useState } from 'react';

const BusinessPlan = () => {
  const [activeTab, setActiveTab] = useState(0);

  const marketData = [
    {
      title: '국내 시장',
      value: '2,500억 원',
      description: '연간 수도미터기 교체 시장 규모',
      icon: 'ri-map-pin-line',
    },
    {
      title: '해외 시장',
      value: '15조 원',
      description: '글로벌 스마트 미터기 시장 규모',
      icon: 'ri-global-line',
    },
    {
      title: '성장률',
      value: '18.5%',
      description: '연평균 시장 성장률 (CAGR)',
      icon: 'ri-line-chart-line',
    },
  ];

  const salesMethods = [
    {
      step: '1단계',
      title: '지자체 직접 영업',
      description:
        '광주광역시를 시작으로 전국 지자체 상수도 사업본부 대상 직접 영업 및 제안',
      icon: 'ri-building-line',
      color: 'from-teal-500 to-teal-600',
    },
    {
      step: '2단계',
      title: '대형 건설사 협력',
      description:
        '신규 아파트 및 대형 건물 건설 시 스마트 미터기 일괄 납품 계약',
      icon: 'ri-community-line',
      color: 'from-teal-600 to-cyan-600',
    },
    {
      step: '3단계',
      title: '유통망 확대',
      description:
        '전국 설비 자재 유통업체 및 온라인 B2B 플랫폼 입점',
      icon: 'ri-store-line',
      color: 'from-cyan-500 to-cyan-600',
    },
    {
      step: '4단계',
      title: '해외 수출',
      description: '동남아시아 및 중동 지역 스마트시티 프로젝트 참여',
      icon: 'ri-ship-line',
      color: 'from-cyan-600 to-teal-700',
    },
  ];

  const productionPlan = [
    {
      year: '2024년',
      target: '10,000대',
      market: '광주광역시 시범 사업',
      revenue: '15억 원',
    },
    {
      year: '2025년',
      target: '50,000대',
      market: '전남북 지역 확대',
      revenue: '75억 원',
    },
    {
      year: '2026년',
      target: '200,000대',
      market: '전국 주요 도시',
      revenue: '300억 원',
    },
    {
      year: '2027년',
      target: '500,000대',
      market: '전국 + 해외 진출',
      revenue: '750억 원',
    },
  ];

  const tabs = ['시장 현황', '영업 전략', '생산 계획'];

  return (
    <section
      id="business-plan"
      className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 섹션 헤더 */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block px-5 py-2 bg-teal-50 border border-teal-200 rounded-full mb-4">
            <span className="text-teal-600 text-xs md:text-sm font-bold uppercase tracking-wider">Business Plan</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-3 md:mb-4">
            사업 계획 및 영업 전략
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto font-medium">
            체계적인 시장 진입 전략과 단계별 성장 계획으로 스마트 수도미터기 시장을
            선도합니다
          </p>
        </div>

        {/* 모바일 탭 네비게이션 */}
        <div className="flex md:hidden bg-gray-100 rounded-full p-1 mb-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`flex-1 py-2 px-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === index
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'text-gray-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* 시장 현황 */}
        <div
          className={`mb-12 md:mb-20 ${
            activeTab !== 0 ? 'hidden md:block' : ''
          }`}
        >
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center hidden md:block">
            영업 및 시장 현황
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
            {marketData.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-5 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-12 md:w-16 h-12 md:h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                  <i
                    className={`${item.icon} text-2xl md:text-3xl text-white`}
                  ></i>
                </div>
                <h4 className="text-sm md:text-lg font-semibold text-gray-700 mb-1 md:mb-2">
                  {item.title}
                </h4>
                <div className="text-2xl md:text-4xl font-bold text-teal-600 mb-2 md:mb-3">
                  {item.value}
                </div>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 주요 영업 방법 */}
        <div
          className={`mb-12 md:mb-20 ${
            activeTab !== 1 ? 'hidden md:block' : ''
          }`}
        >
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center hidden md:block">
            주요 영업 방법
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {salesMethods.map((method, index) => (
              <div
                key={index}
                className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div
                  className={`w-10 md:w-14 h-10 md:h-14 bg-gradient-to-br ${method.color} rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <i
                    className={`${method.icon} text-lg md:text-2xl text-white`}
                  ></i>
                </div>
                <div className="text-xs md:text-sm font-bold text-teal-600 mb-1 md:mb-2">
                  {method.step}
                </div>
                <h4 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-3">
                  {method.title}
                </h4>
                <p className="text-[10px] md:text-sm text-gray-600 leading-relaxed">
                  {method.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 추가사업 생산 계획 */}
        <div
          className={`${
            activeTab !== 2 ? 'hidden md:block' : ''
          }`}
        >
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center hidden md:block">
            추가사업 생산 계획
          </h3>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px]">
                <thead>
                  <tr className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      연도
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      생산 목표
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      목표 시장
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      예상 매출
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {productionPlan.map((plan, index) => (
                    <tr
                      key={index}
                      className={`${
                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                      } hover:bg-teal-50 transition-colors duration-200`}
                    >
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-bold text-teal-600">
                        {plan.year}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold text-gray-900">
                        {plan.target}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-700">
                        {plan.market}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-bold text-teal-600">
                        {plan.revenue}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 생산 계획 하이라이트 */}
          <div className="mt-6 md:mt-8 grid grid-cols-3 gap-3 md:gap-6">
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 md:p-6 text-white">
              <div className="text-[10px] md:text-sm font-semibold mb-1 md:mb-2 opacity-90">
                4년간 누적 생산량
              </div>
              <div className="text-lg md:text-3xl font-bold">760,000대</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 md:p-6 text-white">
              <div className="text-[10px] md:text-sm font-semibold mb-1 md:mb-2 opacity-90">
                4년간 누적 매출
              </div>
              <div className="text-lg md:text-3xl font-bold">1,140억 원</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 md:p-6 text-white">
              <div className="text-[10px] md:text-sm font-semibold mb-1 md:mb-2 opacity-90">
                연평균 성장률
              </div>
              <div className="text-lg md:text-3xl font-bold">187%</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessPlan;
